import { useState, useEffect } from "react";
import axios from "axios";
import OrdersTab from "./admin/OrdersTab";
import UsersTab from "./admin/UsersTab";
import ProductsTab from "./admin/ProductsTab";
import RoutesTab from "./admin/RoutesTab";
import AuditLogsTab from "./admin/AuditLogsTab";
import ArchivedTab from "./admin/ArchivedTab";
import DashboardTab from "./admin/DashboardTab";
import SettingsTab from "./admin/SettingsTab";
import DispatchTab from "./admin/DispatchTab";
import ReceivingTab from "./admin/ReceivingTab";
import InventoryTab from "./admin/InventoryTab";
import MaintenanceTab from "./admin/MaintenanceTab";
import AnalyticsTab from "./admin/AnalyticsTab";
import Sidebar from "@/components/Sidebar";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import NotificationBell from "@/components/NotificationBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, KeyRound, Menu } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function AdminDashboard({ user, onLogout }) {
  const [ordersData, setOrdersData] = useState({ groups: {}, group_by: "date" });
  const [products, setProducts] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupBy, setGroupBy] = useState("date");
  
  // Ordered list of tab IDs ↔ required permissions, mirroring the Sidebar's
  // own `allMenuItems` list. We use this to pick a sensible default tab on
  // mount so a Staff without `dashboard.view` doesn't land on a blank "you
  // can't see this" screen.
  const tabPermMap = [
    { id: "dashboard", perm: "dashboard.view" },
    { id: "orders",    perm: "orders.view" },
    { id: "picking",   perm: "dispatch.manage" },
    { id: "receiving", perm: "receiving.manage" },
    { id: "inventory", perm: "inventory.manage" },
    { id: "audit",     perm: "audit.view" },
    { id: "analytics", perm: "analytics.view" },
    { id: "archived",  perm: "archived.view" },
    { id: "settings",  perm: [
      "users.manage_hotels", "users.manage_staff",
      "products.manage", "routes.manage", "company.manage", "announcements.manage",
    ] },
  ];

  const userCanSee = (perm) => {
    if (user?.role === "admin") return true;
    if (user?.role !== "manager") return false;
    const owned = Array.isArray(user?.permissions) ? user.permissions : [];
    if (Array.isArray(perm)) return perm.some((p) => owned.includes(p));
    return owned.includes(perm);
  };

  // First tab the user is allowed to see — if NOTHING matches (Staff with
  // zero perms), use a "none" sentinel so every gated render block above
  // skips and the friendly empty-state below shows instead of silently
  // mounting an unauthorized OrdersTab.
  const initialTab = (tabPermMap.find((t) => userCanSee(t.perm)) || { id: "none" }).id;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [ordersInitialFilter, setOrdersInitialFilter] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Per-tab remount counter. Incremented every time the user clicks a sidebar
  // item (even one that's already active) so the tab component remounts —
  // wipes any drill-down state (selected order, locked bag, scroll position)
  // and re-fires data fetches via useEffect. Matches the user expectation
  // that "clicking the sidebar takes me back to the top of that page."
  const [tabKeys, setTabKeys] = useState({
    dashboard: 0,
    orders: 0,
    picking: 0,
    receiving: 0,
    inventory: 0,
    analytics: 0,
    audit: 0,
    archived: 0,
    settings: 0,
  });
  
  // Notification states
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchAllData();
    // Clear any stale notifications on mount
    setNotifications([]);

    // Real-time orders subscription via Supabase Realtime (replaces 10s polling).
    // Fires within ~100ms whenever an order row is inserted or its status changes.
    const channel = supabase
      .channel("orders-stream")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "orders" },
        (payload) => {
          const order = payload.new || {};
          // Refresh dashboard data so the new order appears in tabs.
          fetchAllData();
          if (order.status === "pending") {
            const notification = {
              id: Date.now(),
              title: "New Order Alert",
              message: `New order from ${order.hotel_name || "hotel"} — please process`,
              timestamp: new Date().toISOString(),
              read: false,
              type: "new_order",
            };
            setNotifications((prev) => [notification, ...prev]);
            playNotificationSound();
            toast.info("New Order Received", {
              description: `${order.hotel_name || "Hotel"} • ${order.order_number || ""}`.trim(),
              action: {
                label: "View",
                onClick: () => {
                  setOrdersInitialFilter("pending");
                  fetchOrders();
                  setTimeout(() => setActiveTab("orders"), 0);
                },
              },
            });
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "orders" },
        () => {
          // Any status change refreshes the visible data
          fetchAllData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const playNotificationSound = () => {
    // Create a simple beep sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };
  
  const handleNotificationClick = (notification) => {
    // Mark as read
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );

    // Navigate to orders page
    if (notification.type === 'new_order') {
      setOrdersInitialFilter('pending');
      // Always re-fetch orders so the just-notified order is in ordersData
      // (the polling snapshot may have been stale or fetchAllData may have failed).
      fetchOrders();
      setTimeout(() => setActiveTab("orders"), 0);
    }
  };
  
  const handleClearAllNotifications = () => {
    setNotifications([]);
  };

  const fetchAllData = async () => {
    // Fire all four resource loads in parallel — none depends on the others.
    // Sequential awaits used to take ~6s; parallel cuts it to ~2s.
    const headers = getAuthHeader();
    const [ordersRes, productsRes, routesRes, usersRes] = await Promise.allSettled([
      axios.get(`${API}/orders?group_by=${groupBy}`, { headers }),
      axios.get(`${API}/products`, { headers }),
      axios.get(`${API}/routes`, { headers }),
      axios.get(`${API}/users`, { headers }),
    ]);

    if (ordersRes.status === "fulfilled") {
      setOrdersData(ordersRes.value.data);
    } else {
      console.error("Failed to load orders:", ordersRes.reason);
      toast.error("Failed to load orders");
    }
    if (productsRes.status === "fulfilled") {
      setProducts(productsRes.value.data);
    }
    if (routesRes.status === "fulfilled") {
      setRoutes(routesRes.value.data);
    }
    if (usersRes.status === "fulfilled") {
      setUsers(usersRes.value.data);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?group_by=${groupBy}`, { headers: getAuthHeader() });
      setOrdersData(response.data);
    } catch (error) {
      toast.error("Failed to load orders");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status },
        { headers: getAuthHeader() }
      );
      toast.success("Order status updated");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handleGroupByChange = async (newGroupBy) => {
    setGroupBy(newGroupBy);
    try {
      const response = await axios.get(`${API}/orders?group_by=${newGroupBy}`, { headers: getAuthHeader() });
      setOrdersData(response.data);
    } catch (error) {
      toast.error("Failed to load orders");
    }
  };
  
  const handleTabChange = (newTab) => {
    // Always bump the tab's key so the component remounts — even when the
    // user clicks the currently-active sidebar item. This resets any inner
    // drill-down (e.g. Dispatch → bag detail) and re-fires data fetches.
    setTabKeys((prev) => ({ ...prev, [newTab]: (prev[newTab] || 0) + 1 }));
    // Orders tab additionally needs an explicit refetch because its data
    // lives in the parent (ordersData).
    if (newTab === "orders") {
      fetchOrders();
    }
    setActiveTab(newTab);
  };

  return (
    <div className="min-h-screen flex bg-slate-100 text-brand-navy">
      {/* Left Sidebar — desktop fixed, mobile drawer */}
      <Sidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        userRole={user?.role || "customer"}
        userPermissions={user?.permissions || []}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-10 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8 py-3 lg:py-5">
            <div className="flex items-center justify-between gap-2 lg:gap-4">
              <div className="flex items-center gap-2 min-w-0 flex-shrink">
                <Button
                  onClick={() => setMobileNavOpen(true)}
                  data-testid="mobile-nav-toggle"
                  variant="ghost"
                  size="icon"
                  className="lg:hidden shrink-0"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>
                <div className="min-w-0">
                  <h1 className="font-display text-lg sm:text-xl lg:text-2xl font-bold text-brand-navy tracking-tight truncate">Admin Dashboard</h1>
                  <p className="text-xs sm:text-sm text-neutral-500 truncate mt-0.5 hidden sm:block">
                    Welcome, <span className="text-brand-navy font-medium">{user?.hotel_name || user?.username}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
                <NotificationBell
                  notifications={notifications}
                  onNotificationClick={handleNotificationClick}
                  onClearAll={handleClearAllNotifications}
                />
                <Button
                  onClick={() => setChangePasswordOpen(true)}
                  data-testid="change-password-button"
                  variant="outline"
                  className="flex items-center gap-2 border-neutral-300 hover:border-brand-navy hover:bg-brand-sky-soft transition-colors rounded-sm px-2 sm:px-3"
                >
                  <KeyRound className="w-4 h-4" />
                  <span className="hidden md:inline">Password</span>
                </Button>
                <Button
                  onClick={onLogout}
                  data-testid="logout-button"
                  variant="outline"
                  className="flex items-center gap-2 border-neutral-300 hover:border-brand-navy hover:bg-brand-navy hover:text-white transition-colors rounded-sm px-2 sm:px-3"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {/* Announcement Banner (full-width edge to edge) */}
          <AnnouncementBanner />

          <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-8 fade-in">
          {activeTab === "dashboard" && userCanSee("dashboard.view") && (
            <DashboardTab 
              key={tabKeys.dashboard}
              products={products} 
              onNavigateToOrders={(filter) => {
                // Use setTimeout to ensure state updates in correct order
                setOrdersInitialFilter(filter);
                setTimeout(() => setActiveTab("orders"), 0);
              }}
            />
          )}

          {activeTab === "orders" && userCanSee("orders.view") && (
            <OrdersTab 
              ordersData={ordersData} 
              updateOrderStatus={updateOrderStatus} 
              groupBy={groupBy} 
              onGroupByChange={handleGroupByChange}
              user={user}
              users={users}
              products={products}
              fetchAllData={fetchAllData}
              initialStatusFilter={ordersInitialFilter}
              onFilterApplied={() => setOrdersInitialFilter(null)}
              key={tabKeys.orders}
            />
          )}

          {activeTab === "audit" && userCanSee("audit.view") && (
            <AuditLogsTab key={tabKeys.audit} />
          )}

          {activeTab === "picking" && userCanSee("dispatch.manage") && (
            <DispatchTab key={tabKeys.picking} />
          )}

          {activeTab === "receiving" && userCanSee("receiving.manage") && (
            <ReceivingTab key={tabKeys.receiving} />
          )}

          {activeTab === "inventory" && userCanSee("inventory.manage") && (
            <InventoryTab key={tabKeys.inventory} products={products} />
          )}

          {activeTab === "maintenance" && userCanSee("inventory.manage") && (
            <MaintenanceTab />
          )}

          {activeTab === "analytics" && userCanSee("analytics.view") && (
            <AnalyticsTab key={tabKeys.analytics} />
          )}

          {activeTab === "archived" && userCanSee("archived.view") && (
            <ArchivedTab key={tabKeys.archived} onUnarchive={fetchAllData} />
          )}

          {activeTab === "settings" && userCanSee([
            "users.manage_hotels", "users.manage_staff",
            "products.manage", "routes.manage", "company.manage", "announcements.manage",
          ]) && (
            <SettingsTab 
              key={tabKeys.settings}
              products={products} 
              users={users} 
              routes={routes} 
              onUpdate={fetchAllData}
              currentUser={user}
            />
          )}

          {/* Friendly empty-state when the user has zero perms — the Sidebar
              renders zero menu items and every tab block above is gated, so
              the main area would be a blank white pane. Show a clear message
              so the user knows it's a config issue, not a broken app. */}
          {(!tabPermMap.some((t) => userCanSee(t.perm))) && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-6 py-8 max-w-md">
                <h2 className="text-lg font-display font-bold text-brand-navy mb-2">
                  No access yet
                </h2>
                <p className="text-sm text-muted-foreground">
                  Your account doesn&apos;t have any permissions assigned. Please
                  ask an administrator to grant you access from
                  <span className="whitespace-nowrap"> Settings → Users</span>.
                </p>
              </div>
            </div>
          )}
          </div>
        </main>
      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
